import { useEffect, useMemo, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Checkbox } from "@/components/ui/checkbox";
import { getDurationInMinutes } from "@/utils/timeFormatting";
import {
  isPlanningKey,
  useUpdateMeasurementRequestPriority,
  useUpdateWaferRequestPriority,
} from "@/hooks/useRequestPlanning";
import { useSortableTable } from "@/hooks/useSortableTable";
import { PlanningMeasurementRequest } from "@/types/planning";
import { deepEqual } from "@/lib/utils";
import { useIsAdmin } from "@/permissions";
import { DraggableRow } from "./components/DraggableRow";
import { flattenWaferRequests, isWaferRequest } from "./MeasurementRequestsPlanningTable.functions";
import { DragHandle } from "./components/DragHandle";
import { useSWRConfig } from "swr";

type MeasurementRequestsPlanningTableProps = {
  measurementSystem: string;
  onWaferSelectionChange: (selected: Set<string>) => void;
  rows: PlanningMeasurementRequest[];
  selectedWafers: Set<string>;
  showCheckboxes: boolean;
  status: "MEASURED" | "SCHEDULED";
  waferProgressPerRequest: Map<number, { backlog: number; scheduled: number; measured: number }>;
};
const MeasurementRequestsPlanningTable = ({
  measurementSystem,
  onWaferSelectionChange,
  rows,
  selectedWafers,
  showCheckboxes,
  status,
  waferProgressPerRequest,
}: MeasurementRequestsPlanningTableProps) => {
  const previousRowsRef = useRef<PlanningMeasurementRequest[]>([]);
  const [data, setData] = useState<PlanningMeasurementRequest[]>(
    [...rows].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
  );
  const [waferRequestsByRequestId, setWaferRequestsByRequestId] = useState<
    Record<number, number[]>
  >(() => flattenWaferRequests(rows));

  const [expandedRows, setExpandedRows] = useState<Set<number>>(() => new Set());

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const allowCheckAll = useIsAdmin();
  const { mutate } = useSWRConfig();

  const { sortedData, handleSort, renderSortIcon } = useSortableTable(
    data,
    {
      name: (row) => row.name.toLowerCase(),
      imecLotId: (row) => row.imecLotId?.toLowerCase() || "",
      mask: (row) => row.mask.name.toLowerCase(),
      measurementCapability: (row) => row.testLabel.measurementCapability?.name.toLowerCase() || "",
      requester: (row) => row.createdBy.toLowerCase(),
      requestDate: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
      time: (row) =>
        row.waferRequests.reduce((total, wafer) => total + wafer.estimatedTime * wafer.dieCount, 0),
      actualTotalDuration: (row) =>
        row.waferRequests.reduce((total, wafer) => {
          if (
            wafer.status === "SCHEDULED" ||
            wafer.status === "TO_SCHEDULE" ||
            wafer.status === "MEASURED" ||
            wafer.status === "MEASURING"
          ) {
            return total;
          }
          const actualDuration = getDurationInMinutes(wafer.startedAt, wafer.completedAt);
          return total + actualDuration;
        }, 0),
    },
    status === "MEASURED" ? { defaultColumn: "requestDate", defaultDirection: "asc" } : undefined,
  );

  const { trigger: updateMeasurementRequestPriority } = useUpdateMeasurementRequestPriority();
  const { trigger: updateWaferRequestPriority } = useUpdateWaferRequestPriority();

  const [rowsPerPage, setRowsPerPage] = useState("25");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = Number(rowsPerPage);
  const totalCount = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const page = Math.min(currentPage, totalPages);
  const visibleRows =
    status === "MEASURED" ? sortedData.slice((page - 1) * pageSize, page * pageSize) : sortedData;

  if (status === "MEASURED" && currentPage !== page) {
    setCurrentPage(page);
  }

  const measurementRequestRows = useMemo(() => {
    return sortedData.map((r) => r.id);
  }, [sortedData]);

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active.id || !over?.id) return;

    const activeIsWaferRequest = isWaferRequest(active.id);
    const overIsWaferRequest = isWaferRequest(over.id);

    // Prevent dropping of incompatible drag objects
    if (activeIsWaferRequest !== overIsWaferRequest) return;

    // Check which event type we received.
    // Either: WaferRequest-$requestId-$waferId or $id
    if (activeIsWaferRequest) {
      if (active.id !== over.id) {
        const [, activeReqId, wafId] = String(active.id).split("-");
        const [, overReqId, overWafId] = String(over.id).split("-");

        const requestId = Number(activeReqId);
        const overRequestId = Number(overReqId);
        const waferId = Number(wafId);
        const waferIdToSwapWith = Number(overWafId);

        if (requestId !== overRequestId) {
          return;
        }
        setWaferRequestsByRequestId((currentState) => {
          const requestData = currentState[requestId];

          if (!requestData) {
            return currentState;
          }

          const oldIndex = requestData.indexOf(waferId);
          const newIndex = requestData.indexOf(waferIdToSwapWith);

          if (oldIndex === -1 || newIndex === -1) {
            return currentState;
          }
          return {
            ...currentState,
            [requestId]: arrayMove([...requestData], oldIndex, newIndex),
          };
        });
        await updateWaferRequestPriority({
          wafer_id: waferId,
          wafer_id_to_swap_with: waferIdToSwapWith,
        });
        await mutate(isPlanningKey);
      }
    } else {
      if (active.id !== over.id) {
        const oldIndex = measurementRequestRows.indexOf(active.id as number);
        const newIndex = measurementRequestRows.indexOf(over.id as number);

        const reordered = arrayMove([...sortedData], oldIndex, newIndex);
        const reorderedWithPriorities = reordered.map((row, index) => ({
          ...row,
          priority: index + 1,
        }));

        setData(reorderedWithPriorities);

        // Keep current value to avoid triggering the useEffect that
        // wold otherwise revert the optimistic local state update.
        previousRowsRef.current = rows;
        setExpandedRows(new Set());
        await updateMeasurementRequestPriority({
          measurement_request_id: active.id as number,
          new_priority: over?.id as number,
        });
        await mutate(isPlanningKey);
      }
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const toggleRow = (requestId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId);
    } else {
      newExpanded.add(requestId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSelectAll = (checked: boolean) => {
    const nextSelectedWafers = new Set<string>();

    if (checked) {
      visibleRows.forEach((request) => {
        request.waferRequests.forEach((wafer) => {
          nextSelectedWafers.add(`${request.id}-${wafer._id}`);
        });
      });
    }

    onWaferSelectionChange(nextSelectedWafers);
  };

  const allWafersSelected =
    visibleRows.length > 0 &&
    visibleRows.every((request) => {
      return (
        request.waferRequests.length === 0 ||
        request.waferRequests.every((wafer) => selectedWafers.has(`${request.id}-${wafer._id}`))
      );
    });

  const selectedRow = useMemo(() => {
    if (!activeId) return null;

    if (activeId.toString().startsWith("WaferRequest")) {
      const [, reqId, waferId] = activeId.toString().split("-");

      const request = data.find((row) => row.id === Number(reqId));

      if (request) {
        return request.waferRequests.find((w) => w._id === Number(waferId)) ?? null;
      }

      return null;
    }

    return data.find((row) => row.id === activeId);
  }, [activeId, data]);

  useEffect(() => {
    if (!deepEqual(rows, previousRowsRef.current)) {
      const sortedRows = [...rows].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

      setData(sortedRows);
      setWaferRequestsByRequestId(flattenWaferRequests(sortedRows));

      previousRowsRef.current = [...sortedRows];
    }
  }, [rows]);

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      onDragStart={handleDragStart}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={visibleRows.map((row) => row.id)}
        strategy={verticalListSortingStrategy}
      >
        <Table>
          <TableHeader>
            <TableRow className="h-8">
              {status !== "MEASURED" && <TableHead className="w-12 h-8" />}
              {showCheckboxes && (
                <TableHead className="w-px h-8 pl-0 pr-2">
                  <Checkbox
                    checked={allWafersSelected}
                    disabled={!allowCheckAll}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
              )}
              <TableHead className="w-12 h-8" />
              {status === "MEASURED" ? (
                <SortableTableHead
                  column="name"
                  label="Name"
                  onSort={handleSort}
                  renderSortIcon={renderSortIcon}
                />
              ) : (
                <TableHead className="h-8">Name</TableHead>
              )}
              {status === "MEASURED" ? (
                <SortableTableHead
                  column="imecLotId"
                  label="Lot Id"
                  onSort={handleSort}
                  renderSortIcon={renderSortIcon}
                />
              ) : (
                <TableHead className="h-8">Lot Id</TableHead>
              )}
              {status === "MEASURED" ? (
                <SortableTableHead
                  column="mask"
                  label="Mask"
                  onSort={handleSort}
                  renderSortIcon={renderSortIcon}
                />
              ) : (
                <TableHead className="h-8">Mask</TableHead>
              )}
              {status === "MEASURED" ? (
                <SortableTableHead
                  column="measurementCapability"
                  label="Measurement Capability"
                  onSort={handleSort}
                  renderSortIcon={renderSortIcon}
                />
              ) : (
                <TableHead className="h-8 max-w-36">Measurement Cap.</TableHead>
              )}
              {status === "MEASURED" ? (
                <SortableTableHead
                  column="requester"
                  label="Requester"
                  onSort={handleSort}
                  renderSortIcon={renderSortIcon}
                />
              ) : (
                <TableHead className="h-8">Requester</TableHead>
              )}
              {status === "MEASURED" ? (
                <SortableTableHead
                  column="requestDate"
                  label="Request date"
                  onSort={handleSort}
                  renderSortIcon={renderSortIcon}
                />
              ) : (
                <TableHead className="h-8">Request Date</TableHead>
              )}
              {status === "SCHEDULED" && <TableHead className="h-8">Time</TableHead>}
              {status === "MEASURED" && (
                <SortableTableHead
                  column="actualTotalDuration"
                  label="Act. Tot. Duration"
                  onSort={handleSort}
                  renderSortIcon={renderSortIcon}
                />
              )}
              {status !== "MEASURED" && <TableHead className="h-8">Est. Lot Finish</TableHead>}
              <TableHead className="h-8">Wafers Progress</TableHead>
              <TableHead className="h-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    status === "MEASURED" ? (showCheckboxes ? 8 : 7) : showCheckboxes ? 11 : 10
                  }
                  className="text-center py-8 text-muted-foreground"
                >
                  {measurementSystem === "All"
                    ? `No ${status} requests`
                    : `No ${status} requests for ${measurementSystem}`}
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row) => (
                <DraggableRow
                  expandedRows={expandedRows}
                  key={`measurement-request-${row.id}`}
                  onWaferSelectionChange={onWaferSelectionChange}
                  row={row}
                  selectedWafers={selectedWafers}
                  showCheckboxes={showCheckboxes}
                  status={status}
                  toggleRow={toggleRow}
                  waferProgressPerRequest={waferProgressPerRequest}
                  waferRequestIds={waferRequestsByRequestId[row.id] ?? []}
                />
              ))
            )}
          </TableBody>
        </Table>
      </SortableContext>
      {status === "MEASURED" && (
        <TablePagination
          totalRows={totalCount}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(value) => {
            setRowsPerPage(String(value));
            setCurrentPage(1);
          }}
        />
      )}
      <DragOverlay>
        {activeId && !isWaferRequest(activeId) && selectedRow ? (
          <Table>
            <TableBody>
              <TableRow className="hover:bg-transparent">
                <TableCell className="h-10">
                  <DragHandle isDragging />
                </TableCell>
                <TableCell className="h-10" />
                <TableCell className="h-10" />
                <TableCell className="h-10" />
                <TableCell className="h-10" />
                <TableCell className="h-10" />
                <TableCell className="h-10" />
                <TableCell className="h-10" />
                <TableCell className="h-10" />
                <TableCell className="h-10" />
                <TableCell className="h-10" />
                <TableCell className="h-10" />
              </TableRow>
            </TableBody>
          </Table>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default MeasurementRequestsPlanningTable;
